import React from "react";
import {
    Add,
    InfoOutlined,
    ModeEditOutlineTwoTone,
    NextPlan,
} from "@mui/icons-material";
import {
    Box,
    Button,
    Chip,
    IconButton,
    Popover,
    Tooltip,
    Typography,
} from "@mui/material";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import AppTable from "components/Shared/AppTable";
import TableFilter from "components/Shared/AppTable/TableFilter";
import AppTableDateTime from "components/Shared/AppTableDateTime";

import { useAppMeta, useAuth, useTablePagination } from "hooks";
import { useLazyListShipmentUnLoadingQuery } from "store/services/shipmentUnLoadingApi";
import HighlightedText from "components/Shared/HighlightedText";

const DriverShipmentUnloadingListScreen = () => {
    const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
        useTablePagination();

    const { isDriver, loggedInUser } = useAuth();
    const navigate = useNavigate(); // Add useNavigate

    const [searchParams] = useSearchParams();
    const keyword = searchParams.get("search") || "";

    const breadcrumbs = [{ title: "Shipment Unloading" }];

    const columns = [
        {
            name: "ID",
            selector: (row) => row?.unLoadingId,
            cell: (row) => {
                const displayId = row?.unLoadingId;
                const path = !isDriver
                    ? `/app/crm/shipment-unLoading/${row?._id}/detail`
                    : `/app/driver/unloading/${row?._id}/detail`;

                return displayId ? (
                    <Link
                        title={displayId}
                        to={path}
                        className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
                    >
                        <HighlightedText text={displayId} highlight={keyword} />
                    </Link>
                ) : (
                    "N/A"
                );
            },
        },
        {
            name: "Loading ID",
            selector: (row) =>
                (Array.isArray(row?.loading) ? row.loading : [row?.loading].filter(Boolean))
                    .map((l) => l?.loadingId)
                    .filter(Boolean)
                    .join(", "),
            cell: (row) => {
                const loadings = Array.isArray(row?.loading) ? row.loading : [row?.loading].filter(Boolean);
                const ids = loadings.map((l) => l?.loadingId).filter(Boolean);
                return ids.length ? ids.join(", ") : "N/A";
            },
        },
        {
            name: "Shipment ID",
            selector: (row) => row.shipment,
            cell: (row) => {
                // eslint-disable-next-line react-hooks/rules-of-hooks
                const [anchorEl, setAnchorEl] = React.useState(null);
                // eslint-disable-next-line react-hooks/rules-of-hooks
                const [selectedShipments, setSelectedShipments] = React.useState([]);

                const shipments = Array.isArray(row.shipment) ? row.shipment : [];
                const visibleShipments = shipments.slice(0, 2);
                const remainingCount = shipments.length - visibleShipments.length;

                const handleChipClick = (event, allShipments) => {
                    setAnchorEl(event.currentTarget);
                    setSelectedShipments(allShipments);
                };

                const handleClose = () => {
                    setAnchorEl(null);
                };

                const open = Boolean(anchorEl);

                return shipments.length > 0 ? (
                    <>
                        <Box display="flex" alignItems="center" flexWrap="wrap" gap={0.5}>
                            {visibleShipments.map((shipment, index) => {
                                const shiId = shipment?.shipmentId
                                    ?.replace(/(.{4})/g, "$1-")
                                    .slice(0, -1);
                                return (
                                    <span key={index} style={{ fontSize: "0.9rem" }}>
                                        <HighlightedText
                                            text={shipment.IsReturning ? `${shiId} (R)` : shiId}
                                            highlight={keyword}
                                        />
                                        {index !== visibleShipments.length - 1 && ","}
                                    </span>
                                );
                            })}
                            {remainingCount > 0 && (
                                <Tooltip title="Click to view all Shipment IDs">
                                    <Chip
                                        label={`+${remainingCount}`}
                                        size="small"
                                        sx={{
                                            marginLeft: "4px",
                                            height: "18px",
                                            fontSize: "10px",
                                            backgroundColor: "#e0e0e0",
                                            color: "#333",
                                            cursor: "pointer",
                                        }}
                                        onClick={(e) => handleChipClick(e, shipments)}
                                    />
                                </Tooltip>
                            )}
                        </Box>
                        <Popover
                            open={open}
                            anchorEl={anchorEl}
                            onClose={handleClose}
                            anchorOrigin={{
                                vertical: "bottom",
                                horizontal: "left",
                            }}
                            transformOrigin={{
                                vertical: "top",
                                horizontal: "left",
                            }}
                        >
                            <Box p={2} minWidth={100}>
                                {selectedShipments.map((shipment, index) => (
                                    <Typography
                                        key={index}
                                        variant="body2"
                                        sx={{ wordBreak: "break-word" }}
                                    >
                                        <HighlightedText
                                            text={
                                                shipment.IsReturning
                                                    ? `${shipment.shipmentId} (R)`
                                                    : shipment.shipmentId
                                            }
                                            highlight={keyword}
                                        />
                                    </Typography>
                                ))}
                            </Box>
                        </Popover>
                    </>
                ) : (
                    "N/A"
                );
            },
        },
        {
            name: "Unloading Time",
            selector: (row) => <AppTableDateTime date={row?.unloadingTime} />,
        },
                {
            name: "Transit Branch",
            selector: (row) => row?.transitBranchName ? `Transit (${row.transitBranchName})` : "N/A",
            cell: (row) => row?.transitBranchName ? `Transit (${row.transitBranchName})` : "Direct"
        },
        {
            name: "Created At",
            selector: (row) => <AppTableDateTime date={row?.createdAt} />,
        },
        {
            name: "Actions",
            cell: (row) => {
                const unloadedShipments = (row.shipment || []).filter(
                    (shipment) => shipment.status === "Unloaded"
                );

                const isCreateDeconsolidationDisabled = unloadedShipments.length === 0;

                return (
                    <Box display="flex" alignItems="center">
                        <Link
                            to={
                                !isDriver
                                    ? `/app/crm/shipment-unLoading/${row?._id}/detail`
                                    : `/app/driver/unloading/${row?._id}/detail`
                            }
                        >
                            <IconButton
                                size="small"
                                aria-label="View Shipment Unloading"
                                color="primary"
                            >
                                <InfoOutlined />
                            </IconButton>
                        </Link>

                        {!isDriver && (
                            <Link to={`/app/crm/shipment-unLoading/${row?._id}/edit`}>
                                <IconButton size="small" aria-label="Edit Shipment Unloading">
                                    <ModeEditOutlineTwoTone />
                                </IconButton>
                            </Link>
                        )}

                        {!isDriver && (
                            <Tooltip
                                title={
                                    isCreateDeconsolidationDisabled
                                        ? "No Unloaded shipments available"
                                        : "Create Deconsolidation"
                                }
                            >
                                <span>
                                    <IconButton
                                        size="small"
                                        color="primary"
                                        aria-label="Create Deconsolidation"
                                        onClick={() => {
                                            navigate("/app/crm/deconsolidation/create", {
                                                state: {
                                                    unloading: {
                                                        _id: row._id,
                                                        unLoadingId: row.unLoadingId,
                                                    },
                                                    shipments: row.shipment || [],
                                                },
                                            });
                                        }}
                                        disabled={isCreateDeconsolidationDisabled}
                                    >
                                        <NextPlan />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        )}
                    </Box>
                );
            },
            ignoreRowClick: true,
            right: true,
        },
    ];

    // RTK Query
    const [
        fetchShipmentUnLoading,
        { data: shipmentUnLoadingRes, isLoading, isFetching, error },
    ] = useLazyListShipmentUnLoadingQuery();

    React.useEffect(() => {
        fetchShipmentUnLoading({
            page: currentPage,
            driverId: loggedInUser.typeRef,
            limit: perPage,
            ...(keyword && { keyword }),
        });
    }, [
        currentPage,
        loggedInUser,
        fetchShipmentUnLoading,
        keyword,
        perPage,
    ]);

    useAppMeta(`Shipment Unloading`);

    return (
        <BreadcrumbLayout breadcrumbs={breadcrumbs} headerActions={<PageActions />}>
            <BreadcrumbLayout.Error error={error} />
            <BreadcrumbLayout.Paper>
                <TableFilter
                    searchLabel="Unloading ID / Shipment ID"
                    disabled={isLoading || isFetching}
                />
                <AppTable
                    columns={columns}
                    data={shipmentUnLoadingRes?.data ?? []}
                    progressPending={isLoading || isFetching}
                    paginationServer
                    paginationPerPage={perPage}
                    paginationTotalRows={shipmentUnLoadingRes?.totalItems ?? 0}
                    paginationDefaultPage={currentPage}
                    onChangePage={handlePageChange}
                    onChangeRowsPerPage={handlePerRowsChange}
                />
            </BreadcrumbLayout.Paper>
        </BreadcrumbLayout>
    );
};

export default DriverShipmentUnloadingListScreen;

const PageActions = () => {
    const { isDriver } = useAuth();
    return (
        <>
            {!isDriver && (
                <Link to="/app/crm/shipment-unLoading/create">
                    <Button startIcon={<Add />} variant="contained" color="primary">
                        Add Shipment UnLoading
                    </Button>
                </Link>
            )}
            {isDriver && (
                <div className="flex space-x-4">
                    <Link to="/app/driver/unloading/create">
                        <Button startIcon={<Add />} variant="contained" color="primary">
                            UnLoad and Delivered
                        </Button>
                    </Link>
                    <Link to="/app/driver/driver-unloading/create">
                        <Button startIcon={<Add />} variant="contained" color="primary">
                            UnLoad Shipment
                        </Button>
                    </Link>
                </div>

            )}
        </>
    );
};
